import { memo, useState, useMemo, useCallback } from "react";
import {
  View,
  Text,
  Image,
  TouchableOpacity,
  ActivityIndicator,
  StyleSheet,
  Linking,
  Platform,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";

import { FL, FONT, label } from "../floodlight";

const row = { flexDirection: "row", alignItems: "center" };

// ─── URL parser ───────────────────────────────────────────────────────────────

const IMAGE_EXT_RE = /\.(jpe?g|png|gif|webp|svg|bmp|avif)(\?.*)?$/i;

function parseUrl(rawUrl) {
  try {
    const u = new URL(rawUrl);
    const host = u.hostname.replace(/^www\./, "");

    // Direct image link
    if (IMAGE_EXT_RE.test(u.pathname)) {
      const filename = u.pathname.split("/").pop().split("?")[0];
      const ext = filename.split(".").pop().toUpperCase();
      return {
        platform: "image",
        label: host,
        filename,
        fileExt: ext,
        accentColor: FL.ink,
        iconName: "image-outline",
        description: host,
      };
    }

    // YouTube
    if (host === "youtube.com" || host === "youtu.be") {
      const videoId =
        host === "youtu.be"
          ? u.pathname.slice(1).split("?")[0]
          : u.searchParams.get("v");
      if (videoId) {
        return {
          platform: "youtube",
          label: "YouTube",
          thumbnail: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
          thumbnailFallback: `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`,
          accentColor: "#FF0000",
          iconName: "logo-youtube",
          description: `youtu.be/${videoId}`,
        };
      }
    }

    // Twitch
    if (host === "twitch.tv") {
      const channel = u.pathname.slice(1).split("/").filter(Boolean)[0] ?? "";
      return {
        platform: "twitch",
        label: "Twitch",
        title: channel || null,
        thumbnail: null,
        accentColor: "#9147FF",
        iconName: "logo-twitch",
        description: channel ? `twitch.tv/${channel}` : "twitch.tv",
      };
    }

    // Twitter / X
    if (host === "twitter.com" || host === "x.com") {
      return {
        platform: "twitter",
        label: host === "x.com" ? "X (Twitter)" : "Twitter",
        thumbnail: null,
        accentColor: "#1DA1F2",
        iconName: "logo-twitter",
        description: rawUrl,
      };
    }

    // Generic
    return {
      platform: "generic",
      label: host,
      thumbnail: null,
      accentColor: FL.ink,
      iconName: "link-outline",
      description: rawUrl,
    };
  } catch {
    return null;
  }
}

// ─── ImageDisplay sub-component ───────────────────────────────────────────────

/**
 * Renders a direct image URL at its natural aspect ratio.
 * Calls onSizeLoaded({ width, height }) once the image dimensions are known.
 */
const ImageDisplay = memo(function ImageDisplay({ uri, onSizeLoaded }) {
  const [size, setSize] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const handleLoad = useCallback(
    (e) => {
      const { width, height } = e.nativeEvent.source;
      setSize({ width, height });
      setLoading(false);
      onSizeLoaded?.({ width, height });
    },
    [onSizeLoaded]
  );

  const handleError = useCallback(() => {
    setFailed(true);
    setLoading(false);
  }, []);

  if (failed) return null;

  return (
    // Placeholder container while loading; collapses once the image defines its own height.
    <View style={[styles.imageContainer, !size && styles.imageContainerPlaceholder]}>
      <Image
        source={{ uri }}
        style={[styles.imageFill, !!size && { aspectRatio: size.width / size.height }]}
        resizeMode="contain"
        onLoad={handleLoad}
        onError={handleError}
      />
      {loading && (
        <View style={[StyleSheet.absoluteFill, styles.imageLoadingOverlay]}>
          <ActivityIndicator size="small" color={FL.ink2} />
        </View>
      )}
    </View>
  );
});

// ─── Thumbnail sub-component (video platforms) ────────────────────────────────

const Thumbnail = memo(function Thumbnail({ uri, fallback }) {
  const [src, setSrc] = useState(uri);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const handleError = useCallback(() => {
    if (src === uri && fallback) {
      setSrc(fallback);
    } else {
      setFailed(true);
      setLoading(false);
    }
  }, [src, uri, fallback]);

  if (failed) return null;

  return (
    <View style={styles.thumbContainer}>
      <Image
        source={{ uri: src }}
        style={styles.thumbnail}
        resizeMode="cover"
        onLoad={() => setLoading(false)}
        onError={handleError}
      />
      {loading && (
        <View style={styles.thumbLoading}>
          <ActivityIndicator size="small" color={FL.ink2} />
        </View>
      )}
      {!loading && (
        <View style={styles.playOverlay} pointerEvents="none">
          <View style={styles.playBtn}>
            <Ionicons name="play" size={20} color={FL.onGreen} />
          </View>
        </View>
      )}
    </View>
  );
});

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * EmbedPreview
 *
 * Renders a rich link-preview card for an external URL or video URL.
 * Detects: direct image files, YouTube, Twitch, Twitter/X, and generic links.
 *
 * Props:
 *   url    {string}  The URL to preview (required)
 *   style  {object}  Optional extra style for the outer container
 */
const EmbedPreview = memo(function EmbedPreview({ url, style }) {
  const meta = useMemo(() => parseUrl(url), [url]);
  const [imageSize, setImageSize] = useState(null);

  const handlePress = useCallback(() => {
    if (url) Linking.openURL(url).catch(() => {});
  }, [url]);

  if (!meta) return null;

  const isImage = meta.platform === "image";

  return (
    <TouchableOpacity
      onPress={handlePress}
      activeOpacity={0.75}
      style={[styles.card, { borderLeftColor: meta.accentColor }, style]}
      dataSet={{ flCard: "" }}
      accessibilityRole="link"
      accessibilityLabel={`Open ${meta.label} link`}
    >
      {/* Direct image display */}
      {isImage && (
        <ImageDisplay uri={url} onSizeLoaded={setImageSize} />
      )}

      {/* Video thumbnail (YouTube etc.) */}
      {!isImage && !!meta.thumbnail && (
        <Thumbnail uri={meta.thumbnail} fallback={meta.thumbnailFallback} />
      )}

      {/* Metadata section */}
      <View style={styles.metaSection}>
        {/* Platform / source badge */}
        <View style={[row, { gap: 6, marginBottom: 4 }]}>
          <Ionicons name={meta.iconName} size={13} color={meta.accentColor} />
          <Text style={styles.platformLabel}>
            {meta.label}
          </Text>
          {/* File-type badge for images */}
          {isImage && !!meta.fileExt && (
            <View style={styles.extBadge}>
              <Text style={styles.extBadgeText}>{meta.fileExt}</Text>
            </View>
          )}
        </View>

        {/* Filename (images) or channel title (Twitch) */}
        {!!(meta.filename ?? meta.title) && (
          <Text style={styles.title} numberOfLines={1}>
            {meta.filename ?? meta.title}
          </Text>
        )}

        {/* Image dimensions — shown once the image has loaded */}
        {isImage && !!imageSize && (
          <Text style={styles.imageDimensions}>
            {imageSize.width} × {imageSize.height} px
          </Text>
        )}

        {/* URL / description */}
        {!isImage && (
          <Text style={styles.description} numberOfLines={2}>
            {meta.description}
          </Text>
        )}

        {/* External link hint */}
        <View style={[row, { gap: 4, marginTop: 6 }]}>
          <Ionicons name="open-outline" size={11} color={FL.ink2} />
          <Text style={styles.openHint}>Open link</Text>
        </View>
      </View>
    </TouchableOpacity>
  );
});

export default EmbedPreview;

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  card: {
    marginTop: 8,
    maxWidth: 360,
    overflow: "hidden",
    backgroundColor: FL.card,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: FL.hair,
  },

  // ── Direct image ──────────────────────────────────────────────────
  imageContainer: {
    width: "100%",
    backgroundColor: FL.paper2,
  },
  imageContainerPlaceholder: {
    height: 140,
  },
  imageFill: {
    width: "100%",
  },
  imageLoadingOverlay: {
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: FL.paper2,
  },

  // ── Video thumbnail ───────────────────────────────────────────────
  thumbContainer: {
    width: "100%",
    aspectRatio: 16 / 9,
    backgroundColor: FL.paper2,
    borderBottomWidth: 1,
    borderBottomColor: FL.hair,
  },
  thumbnail: {
    width: "100%",
    height: "100%",
  },
  thumbLoading: {
    ...StyleSheet.absoluteFillObject,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: FL.paper2,
  },
  playOverlay: {
    ...StyleSheet.absoluteFillObject,
    alignItems: "center",
    justifyContent: "center",
  },
  playBtn: {
    width: 48,
    height: 48,
    borderRadius: 999,
    backgroundColor: FL.green, // the accent fill: reads on any thumbnail
    ...(Platform.OS === "web" ? { boxShadow: "0 8px 24px rgba(0,0,0,0.45)" } : {}),
    alignItems: "center",
    justifyContent: "center",
  },

  // ── Metadata ──────────────────────────────────────────────────────
  metaSection: {
    padding: 12,
  },
  platformLabel: {
    ...label(11),
    color: FL.ink,
    fontWeight: "800",
  },
  extBadge: {
    paddingHorizontal: 5,
    paddingVertical: 1,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: FL.rule,
  },
  extBadgeText: {
    fontFamily: FONT,
    fontSize: 9,
    fontWeight: "800",
    color: FL.ink,
    letterSpacing: 0.4,
  },
  title: {
    fontFamily: FONT,
    fontSize: 14,
    fontWeight: "700",
    color: FL.ink,
    marginBottom: 2,
  },
  imageDimensions: {
    fontFamily: FONT,
    fontSize: 11,
    color: FL.muted,
    marginTop: 1,
  },
  description: {
    fontFamily: FONT,
    fontSize: 12,
    color: FL.ink2,
    lineHeight: 17,
  },
  openHint: {
    ...label(10),
    color: FL.ink2,
  },
});
